import { ProductPlanStatus } from "../components/Member/CurrentPlan/constants";
import {
  MemberProduct,
  MemberProductLevelEnum,
  PayChannel,
} from "../features/api/memberApi";

const PAY_CYCLES = {
  MONTH: "MONTH",
  YEAR: "YEAR",
  WEEK: "WEEK",
  LIFETIME: "LIFETIME",
} as const;

type PayCycle = (typeof PAY_CYCLES)[keyof typeof PAY_CYCLES];

const IAP_CHANNELS = new Set<PayChannel>([
  PayChannel.APPLE_IAP,
  PayChannel.GOOGLE_IAP,
]);

export type CompletePlansByType = {
  weekly?: MemberProduct;
  monthly?: MemberProduct;
  yearly?: MemberProduct;
  lifetime?: MemberProduct;
};

const hasActivePaymentConfig = (
  plan: MemberProduct,
  matchChannel: (channel: PayChannel) => boolean,
): boolean =>
  plan.status === ProductPlanStatus.ACTIVE &&
  !!plan.payment_configs?.some(
    (config) =>
      matchChannel(config.channel as PayChannel) &&
      config.status === ProductPlanStatus.ACTIVE,
  );

export const filterActiveMemberPlans = (
  data: MemberProduct[],
  {
    isApp,
    payChannel,
  }: {
    isApp: boolean;
    payChannel: PayChannel;
  },
): MemberProduct[] => {
  if (isApp) {
    return data.filter((plan) =>
      hasActivePaymentConfig(plan, (channel) => channel === payChannel),
    );
  }

  return data.filter((plan) =>
    hasActivePaymentConfig(plan, (channel) => !IAP_CHANNELS.has(channel)),
  );
};

const findExpertPlanByCycle = (
  activePlans: MemberProduct[],
  payCycle: PayCycle,
): MemberProduct | undefined =>
  activePlans.find(
    (plan) =>
      plan.level === MemberProductLevelEnum.EXPERT &&
      plan.pay_cycle === payCycle,
  );

const sanitizePlanEquities = (plan: MemberProduct): MemberProduct => {
  if (!plan.equities) {
    return plan;
  }

  return {
    ...plan,
    equities: plan.equities.filter(
      (equity: { status?: string }) =>
        equity?.status === "OWN" || equity?.status === "",
    ),
  };
};

const VALID_PAY_CYCLES = new Set<string>(Object.values(PAY_CYCLES));

export const buildSubscriptionPlans = (
  data: MemberProduct[],
  activePlans: MemberProduct[],
  billingCycle: string,
): MemberProduct[] => {
  if (!VALID_PAY_CYCLES.has(billingCycle)) {
    return [];
  }

  const payCycle = billingCycle as PayCycle;

  const freePlan = data.find(
    (plan) => plan.level === MemberProductLevelEnum.FREE,
  );
  const completePlan = findExpertPlanByCycle(activePlans, payCycle);

  return [freePlan, completePlan]
    .filter((plan): plan is MemberProduct => !!plan)
    .map(sanitizePlanEquities);
};

const toSanitizedExpertPlan = (
  activePlans: MemberProduct[],
  payCycle: PayCycle,
): MemberProduct | undefined => {
  const plan = findExpertPlanByCycle(activePlans, payCycle);
  return plan ? sanitizePlanEquities(plan) : undefined;
};

export const resolveCompletePlansByType = (
  activePlans: MemberProduct[],
): CompletePlansByType => ({
  weekly: toSanitizedExpertPlan(activePlans, PAY_CYCLES.WEEK),
  monthly: toSanitizedExpertPlan(activePlans, PAY_CYCLES.MONTH),
  yearly: toSanitizedExpertPlan(activePlans, PAY_CYCLES.YEAR),
  lifetime: toSanitizedExpertPlan(activePlans, PAY_CYCLES.LIFETIME),
});

export const resolveSubscriptionPlansFromData = (
  data: MemberProduct[],
  billingCycle: string,
  {
    isApp,
    payChannel,
  }: {
    isApp: boolean;
    payChannel: PayChannel;
  },
): MemberProduct[] => {
  const activePlans = filterActiveMemberPlans(data, { isApp, payChannel });
  return buildSubscriptionPlans(data, activePlans, billingCycle);
};

export const resolveCompletePlansFromData = (
  data: MemberProduct[],
  {
    isApp,
    payChannel,
  }: {
    isApp: boolean;
    payChannel: PayChannel;
  },
): CompletePlansByType => {
  const activePlans = filterActiveMemberPlans(data, { isApp, payChannel });
  return resolveCompletePlansByType(activePlans);
};
